import { useState } from 'react'
import { Link } from 'react-router-dom'
import { FOOTER_LINKS, SOCIAL_LINKS, SITE } from '@/lib/site'
import { useTurnstile } from '@/lib/turnstile'

export function Footer() {
  const [subscribed, setSubscribed] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const ts = useTurnstile()

  async function onSubscribe(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const fd = new FormData(e.currentTarget)
    setBusy(true)
    setError('')
    try {
      const turnstile = await ts.getToken()
      const res = await fetch(`${SITE.wpApi}/mitohacker/v1/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: fd.get('email'), company: fd.get('company'), source: 'footer', turnstile }),
      })
      const json = await res.json().catch(() => ({}))
      if (res.ok && json.ok) setSubscribed(true)
      else setError(json.error || 'Something went wrong. Please try again.')
    } catch {
      setError('Network error. Please try again.')
    } finally {
      ts.reset()
      setBusy(false)
    }
  }

  return (
    <footer className="relative mt-32 border-t border-line bg-surface">
      <div className="container-page py-16">
        <div className="grid gap-12 md:grid-cols-[1.4fr_1fr_1fr_1fr]">
          {/* Brand + newsletter */}
          <div>
            <div className="flex items-center gap-2.5">
              <img src="/brand/mark-white.svg" alt="" width={28} height={28} />
              <span className="font-display text-lg font-semibold">
                <span className="text-mist">MITO</span>
                <span className="text-fog">HACKER</span>
              </span>
            </div>
            <p className="mt-4 max-w-xs text-sm text-fog">
              The science of cellular energy. Evidence-based, honest, and built to be the clearest
              resource on mitochondrial optimization.
            </p>
            {subscribed ? (
              <p className="mt-6 max-w-sm rounded-full border border-energy/30 bg-energy/5 px-4 py-3 text-sm text-mist">
                You're on the list. Thanks for subscribing.
              </p>
            ) : (
              <form className="mt-6 flex max-w-sm items-center gap-2" onSubmit={onSubscribe} aria-label="Newsletter signup">
                <input type="text" name="company" tabIndex={-1} autoComplete="off" aria-hidden className="hidden" />
                <input
                  type="email"
                  name="email"
                  required
                  onFocus={() => void ts.prime()}
                  placeholder="Your email"
                  className="h-11 w-full rounded-full border border-line-strong bg-void px-4 text-sm text-mist placeholder:text-faint focus:border-energy"
                />
                <button
                  type="submit"
                  disabled={busy}
                  className="h-11 shrink-0 rounded-full bg-energy px-5 text-sm font-semibold text-void transition-opacity hover:opacity-90 disabled:opacity-60"
                >
                  {busy ? '…' : 'Join'}
                </button>
              </form>
            )}
            {error && <p className="mt-2 text-xs text-spark">{error}</p>}
            <div ref={ts.ref} className="mt-2" />
          </div>

          {FOOTER_LINKS.map((col) => (
            <div key={col.title}>
              <h2 className="font-display text-xs font-medium uppercase tracking-[0.18em] text-faint">
                {col.title}
              </h2>
              <ul className="mt-4 space-y-2.5">
                {col.links.map((l) => (
                  <li key={l.href}>
                    <Link to={l.href} className="text-sm text-fog transition-colors hover:text-mist">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-14 flex flex-col items-start justify-between gap-6 border-t border-line pt-8 sm:flex-row sm:items-center">
          <p className="text-xs text-faint">
            © {new Date().getFullYear()} {SITE.name}. Educational content only, not medical advice.
          </p>
          <div className="flex items-center gap-4">
            {SOCIAL_LINKS.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener"
                className="text-xs font-medium text-fog transition-colors hover:text-energy"
              >
                {s.label}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  )
}
